import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HeartHandshake, Users } from "lucide-react";
import { IMAGES, TRUST } from "@/content";
import { scrollToId } from "@/components/Navbar";

const LINES = ["અંગ તૂટી શકે,", "હિંમત ક્યારેય નહીં."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-cream pt-[72px]"
    >
      <div className="noise-overlay" />
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-saffron-soft blur-3xl opacity-70" />
      <div className="absolute -bottom-40 -left-24 w-[420px] h-[420px] rounded-full bg-leaf-soft blur-3xl opacity-70" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 lg:gap-6 items-center w-full">
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="inline-flex items-center gap-2 rounded-full border border-leaf/30 bg-leaf-soft px-4 py-1.5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-leaf animate-pulse" />
            <span
              data-testid="hero-eyebrow"
              className="font-head font-semibold text-xs sm:text-sm text-leaf-dark tracking-wide"
            >
              {TRUST.name}
            </span>
          </motion.div>

          <h1
            data-testid="hero-heading"
            className="font-display text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.25] lg:leading-[1.22]"
          >
            {LINES.map((line, i) => (
              <span key={line} className="line-mask pb-1">
                <motion.span
                  className="inline-block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 0.9,
                    delay: 0.35 + i * 0.18,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  {i === 1 ? (
                    <span className="text-saffron">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-6 max-w-xl text-base md:text-lg text-ink/70 leading-relaxed"
          >
            છેલ્લા ૧૭ વર્ષથી રાજકોટમાં ૨૧ કેટેગરીના દિવ્યાંગ બંધુઓ માટે — મફત
            વ્હીલચેર, તબીબી સહાય, શિક્ષણ, રોજગાર અને પેરા સ્પોર્ટ્સ. તમારું એક
            દાન, કોઈનું નવું જીવન.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-donate-button"
              onClick={() => scrollToId("donate")}
              className="flex items-center gap-2.5 rounded-full bg-terracotta px-7 py-3.5 font-head font-bold text-cream text-base shadow-[0_14px_40px_rgb(169,50,38,0.35)] hover:bg-terracotta/90 hover:-translate-y-0.5 transition-all duration-200"
            >
              <HeartHandshake className="w-5 h-5" />
              હમણાં દાન કરો
            </button>
            <button
              data-testid="hero-join-button"
              onClick={() => scrollToId("join")}
              className="flex items-center gap-2.5 rounded-full border-2 border-leaf px-7 py-3 font-head font-bold text-leaf-dark text-base hover:bg-leaf hover:text-cream transition-colors duration-200"
            >
              <Users className="w-5 h-5" />
              સભ્ય બનો
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="overflow-hidden rounded-t-[999px] rounded-b-3xl border-[6px] border-white shadow-[0_30px_80px_rgb(31,41,55,0.18)]">
              <motion.img
                src={IMAGES.hero}
                alt="દિવ્યાંગ વ્યક્તિની સેવા કરતા સ્વયંસેવકો"
                data-testid="hero-image"
                style={{ y: imgY, scale: imgScale }}
                className="w-full h-[420px] sm:h-[520px] object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              className="absolute -bottom-6 -left-4 sm:-left-10 bg-white rounded-2xl shadow-[0_16px_50px_rgb(31,41,55,0.15)] px-5 py-4 border border-stone-100"
            >
              <p className="font-display text-2xl text-saffron">1,000+</p>
              <p className="font-head text-sm font-semibold text-ink/70">
                નોંધાયેલા દિવ્યાંગ સભ્યો
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
